import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { ApiError } from '../lib/api';
import { type EnrollRefusal, enrollRefusal } from '../lib/enroll-refusal';
import { type EnrollmentMint, mintEnrollment } from '../lib/fleet';
import { CodingModelLine } from './CodingModelLine';
import { EnrollRefusalAlert } from './EnrollRefusalAlert';
import { OnboardingCopyButton } from './OnboardingCopyButton';
import { Button } from './ui/Button';

// The box step's enrollment token (owner ruling, PR 7): minted on an explicit
// click, and NEVER painted in full. The fleet dialog reveals the plaintext once;
// the funnel does not, because a first-run page is exactly the one that gets
// screen-shared and screenshotted. The token lives only in this component's
// signal, the screen shows `••••••••wxyz`, and COPY is the one way the
// plaintext leaves — straight to the `read -rsp` prompt of the command above.
// `OnboardingBoxStep.test.tsx` pins that the full token is absent from the text.
//
// A refused clipboard has no on-screen text to fall back to, by design, so the
// refusal says what to do instead (a secure origin) rather than "select it".
//
// Mint mechanics are the fleet dialog's (`EnrollTransportToken`): one token per
// box, single-use with a 1h TTL, so a second machine is a second mint; the mint
// is await-then-assign so a refused repeat keeps the live token on screen; a 402
// is the box budget, framed by `enrollRefusal` (`org_unfunded` by name).

/**
 * `dev_ren_<base64url>` → `••••••••wxyz`: fixed bullets and the last four, which
 * tell two mints apart and nothing more.
 *
 * NOT cut at a separator. The random part is base64url, whose alphabet contains
 * `_` and `-`, so `lastIndexOf('_')` landed inside the secret on about half of
 * real tokens and painted most of it (reviewer BLOCK, PR 7). The prefix is not
 * shown at all: its one definition is `ENROLLMENT_TOKEN_PREFIX` in
 * `@developerz/db`, which this app may not import, and a second literal here
 * would be a copy that drifts. `OnboardingEnrollToken.test.tsx` loops over
 * minted-shape tokens to hold it.
 */
export function maskEnrollToken(token: string): string {
  return `••••••••${token.slice(-4)}`;
}

export type OnboardingEnrollTokenProps = {
  readonly token: string | null;
  /** A mint burns a seat slot whether or not the plaintext is kept — refetch the roster. */
  readonly onMinted: () => void;
};

export function OnboardingEnrollToken(props: OnboardingEnrollTokenProps): JSX.Element {
  const [busy, setBusy] = createSignal(false);
  const [minted, setMinted] = createSignal<EnrollmentMint | null>(null);
  const [count, setCount] = createSignal(0);
  const [refusal, setRefusal] = createSignal<EnrollRefusal | null>(null);

  const mint = async (): Promise<void> => {
    setBusy(true);
    setRefusal(null);
    try {
      setMinted(await mintEnrollment(props.token));
      setCount((n) => n + 1);
      props.onMinted();
    } catch (error) {
      setRefusal(
        error instanceof ApiError && error.status === 402
          ? enrollRefusal(error, t('fleet.addbox.seat_cap'))
          : { kind: 'line', message: t('fleet.addbox.mint_error'), soft: false },
      );
    } finally {
      setBusy(false);
    }
  };

  const mintLabel = (): string =>
    t(
      busy()
        ? 'onboarding.box_token_minting'
        : minted()
          ? 'onboarding.box_token_mint_next'
          : 'onboarding.box_token_mint',
    );

  return (
    <section class="onboarding-token" aria-labelledby="onboarding-token-label">
      <h3 id="onboarding-token-label" class="onboarding-token__label">
        {t('onboarding.box_token_label')}
      </h3>
      <p class="onboarding-token__body">{t('onboarding.box_token_body')}</p>
      <Show when={minted()}>
        {(m) => (
          <div class="onboarding-token__value">
            {/* The bullets mean nothing read aloud; the sr-only line names the
                tail instead (`aria-label` is prohibited on a `code` role). */}
            <code class="onboarding-token__mask" aria-hidden="true">
              {maskEnrollToken(m().enrollment_token)}
            </code>
            <span class="sr-only">
              {t('onboarding.box_token_mask_label', { tail: m().enrollment_token.slice(-4) })}
            </span>
            <Show when={count() > 1}>
              <span class="onboarding-token__ordinal">
                {t('onboarding.box_token_ordinal', { n: count() })}
              </span>
            </Show>
          </div>
        )}
      </Show>
      {/* What to type at the command's model prompt (plan 102 slice 05). */}
      <Show when={minted()?.coding_model}>
        {(cm) => <CodingModelLine model={cm()} exported={false} />}
      </Show>
      <Show when={refusal()}>{(r) => <EnrollRefusalAlert refusal={r()} />}</Show>
      <div class="onboarding-token__actions">
        <Show when={minted()}>
          {(m) => (
            <OnboardingCopyButton
              text={() => m().enrollment_token}
              label={t('onboarding.box_token_copy')}
              copiedLabel={t('onboarding.copied')}
              refusedLabel={t('onboarding.box_token_copy_refused')}
            />
          )}
        </Show>
        <Button
          variant="outline"
          size="sm"
          aria-disabled={busy()}
          onClick={() => {
            if (!busy()) void mint();
          }}
        >
          {mintLabel()}
        </Button>
      </div>
    </section>
  );
}
